import { LitElement } from 'lit';
import { createRef, type Ref } from 'lit/directives/ref.js';
import { tick } from '../../../../../_ui-core_/base/package/util/render';
import { UIModal } from '../../../../../_ui-core_/components/ui-modal/ui-modal';
import App from '../../../..';
import { isCashierRouteUrl } from '../../router/router-types/main-route';
import { storyService } from '../story/story-service';

export abstract class UIModalServiceModal extends LitElement {
  protected readonly _modalRef: Ref<UIModal> = createRef();

  private readonly _handleInnerModalClosed = (ev: Event) => {
    // Only re-dispatch if the event originated from the inner ui-modal (not from ourselves)
    if (ev.target === this) return;
    this.dispatchEvent(new Event(UIModal.CLOSE_EVENT, { bubbles: true, composed: true }));
  };

  abstract close(): void;

  /**
   * Ensures the modal-closed event from the inner ui-modal reaches the modal
   * service's listener on this host element. The composed event from nested
   * shadow DOM does not reliably cross the outer shadow boundary (IGM-3644).
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(UIModal.CLOSE_EVENT, this._handleInnerModalClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(UIModal.CLOSE_EVENT, this._handleInnerModalClosed);
  }
}

/**
 * Every modal type needs to be registered here to be able to track it.
 */
export const ModalType = {
  BestMatchInfo: 'BestMatchInfo',
  CampaignTaC: 'CampaignTaC',
  Club1000Info: 'Club1000Info',
  Compliance: 'Compliance',
  ComplianceInfo: 'ComplianceInfo',
  ConfirmLocationModal: 'ConfirmLocationModal',
  Dev: 'DevModal',
  DevFlavor: 'DevFlavorModal',
  FilterModal: 'FilterModal',
  GameDiscovery: 'GameDiscovery',
  GamificationTeaser: 'GamificationTeaser',
  GenericError: 'GenericError',
  GlobalStatusModal: 'GlobalStatusModal',
  HelpCenter: 'HelpCenter',
  IncentiveModal: 'IncentiveModal',
  LimitIncrease: 'LimitIncrease',
  LimitsEnrolment: 'LimitsEnrolment',
  FreeSpinLogout: 'FreeSpinLogout',
  LoggedOut: 'LoggedOut',
  NotificationPermission: 'NotificationPermission',
  LoginModal: 'LoginModal',
  LoginPromotionalModal: 'LoginPromotionalModal',
  OasisMaintenance: 'OasisMaintenance',
  OfflineModal: 'OfflineModal',
  OptOutModal: 'OptOutModal',
  PanicInfo: 'PanicInfo',
  PanicMode: 'PanicMode',
  PaymentTeaser: 'PaymentTeaser',
  ProductSwitcher: 'ProductSwitcher',
  RewardDetails: 'RewardDetails',
  RgProtection: 'RgProtection',
  SessionTimeLimitReachedModal: 'SessionTimeLimitReachedModal',
  StateDisclaimerModal: 'StateDisclaimerModal',
  WelcomeModal: 'WelcomeModal',
} as const;

export type ModalType = (typeof ModalType)[keyof typeof ModalType];

/**
 * Higher-order (array index) modals are displayed first when multiple modals are active.
 * If a modal with a higher priority is opened, the current modal is queued.
 * If a modal with a lower priority is opened, it is queued.
 * If a modal with the same priority is opened, the current modal is closed and the new one is opened.
 * If a modal is closed, the next modal in the queue is opened.
 * If a modal type is not in the prioritization array, it is queued as a priority 0 modal.
 */
const prioritization: ModalType[] = [
  ModalType.FilterModal,
  ModalType.WelcomeModal,
  ModalType.LoginPromotionalModal,
  ModalType.IncentiveModal,
  ModalType.RewardDetails,
  ModalType.OptOutModal,
  ModalType.CampaignTaC,
  ModalType.LimitIncrease,
  ModalType.ProductSwitcher,
  ModalType.GlobalStatusModal,
  ModalType.HelpCenter,
  // Above all promotional/feature modals, below compliance (CI-688 AC7)
  ModalType.NotificationPermission,
  ModalType.ConfirmLocationModal,
  ModalType.RgProtection,
  ModalType.ComplianceInfo,
  ModalType.Compliance,
  ModalType.LimitsEnrolment, // above compliance, below errors (IGM-4163)
  ModalType.GenericError,
  ModalType.FreeSpinLogout,
  ModalType.SessionTimeLimitReachedModal,
  ModalType.LoggedOut, // Needs to have a higher priority than ActivityCheck modal!
  ModalType.OfflineModal,
  ModalType.Dev,
  ModalType.DevFlavor,
];

// Dropped on cashier routes instead of held. Empty until the skip rules are specified (IGM-4148).
const cashierDroppedModalTypes: ReadonlySet<ModalType> = new Set<ModalType>([]);

// Dev tooling stays usable on the cashier.
const cashierPauseExemptModalTypes: ReadonlySet<ModalType> = new Set<ModalType>([ModalType.Dev, ModalType.DevFlavor]);

type ActiveModal = {
  modal: UIModalServiceModal;
  type: ModalType;
};

/**
 * Modal service to handle modals and stacking of multiple modals.
 */
export class ModalService {
  private _root: LitElement | ShadowRoot;
  private _activeModal: ActiveModal | undefined;
  private _modalQueue: Map<ModalType, UIModalServiceModal> = new Map();
  private readonly _scrollLockConcerns = new Set<string>();
  private _showInProgress = false;
  private _showQueue: Array<{ modal: UIModalServiceModal; type: ModalType }> = [];
  private _toBeShownModal: UIModalServiceModal | null = null;
  private _triggerElement?: HTMLElement;
  private _isSubscribedToRouteChanges = false;
  // Holds modals while the PayTech cashier MFE is shown (IGM-4148).
  private _isPaused = false;
  private readonly _modalActivityListeners = new Set<(hasActiveModal: boolean) => void>();

  // Using WeakMap to allow Garbage Collector completely remove all of the listeners if modal is not in the DOM
  private readonly _modalListeners = new WeakMap<UIModalServiceModal, () => void>();

  constructor(private readonly _cashierDroppedTypes: ReadonlySet<ModalType> = cashierDroppedModalTypes) {}
  /**
   * Closes the modal of the given type, if it is active or in the queue.
   * @param type The type of the modal.
   * @param removeFromDOM Optionally remove it from DOM completely even if it's hidden
   */
  public closeCurrentType(type: ModalType, removeFromDOM = false): Promise<void> {
    window.$app.logger.log(`ModalService: closeCurrentType '${type}', removeFromDOM: '${removeFromDOM}'`);

    if (removeFromDOM) {
      this._removeModalCompletely(type);
    } else {
      this._modalQueue.delete(type);
    }

    this._showQueue = this._showQueue.filter((e) => e.type !== type);

    if (this._activeModal?.type === type) {
      return this._closeActiveModal();
    }
    return Promise.resolve();
  }

  /**
   * Show or queue a modal.
   * @param modal The modal to show.
   * @param type The type of the modal.
   */
  public show(modal: UIModalServiceModal, type: ModalType, trigger?: HTMLElement): void {
    // WARN: Do not log `modal` (DOM element) here — DevTools retains references
    // to logged objects, preventing GC of detached modal subtrees (IGM-3644).
    window.$app.logger.log(`ModalService: show '${type}'`);
    this._subscribeToRouteChanges();
    if (this._isDropped(type)) {
      window.$app.logger.log(`ModalService: on cashier route => drop '${type}'`);
      return;
    }
    if (trigger) {
      this._triggerElement = trigger;
    }

    this._showQueue.push({ modal, type });
    this._processShowQueue();
  }

  /**
   * Returns all modals, which are shown or in queue and match given condition.
   * @param filterFn - A function to filter the modals.
   */
  public getActiveOrQueuedModals(filterFn: (modal: UIModalServiceModal) => boolean): UIModalServiceModal[] {
    const matchingModals: UIModalServiceModal[] = [];
    if (this._toBeShownModal && filterFn(this._toBeShownModal)) {
      matchingModals.push(this._toBeShownModal);
    }
    if (this._activeModal?.modal && filterFn(this._activeModal.modal)) {
      matchingModals.push(this._activeModal.modal);
    }
    matchingModals.push(...[...this._modalQueue.values()].filter(filterFn));
    matchingModals.push(...this._showQueue.map((q) => q.modal).filter(filterFn));
    window.$app.logger.log(`ModalService: getActiveOrQueuedModals(): ${matchingModals.length} modal(s) found`);
    return matchingModals;
  }

  public get activeModal() {
    return this._activeModal;
  }

  public handleBodyScroll(action: 'lock' | 'unlock', concern: BodyScrollLockConcern) {
    window.$app.logger.log(`ModalService: requested scroll ${action} for ${concern}`);
    const wasLocked = this._scrollLockConcerns.size > 0;
    if (action === 'lock') {
      this._scrollLockConcerns.add(concern);
    } else {
      this._scrollLockConcerns.delete(concern);
    }
    const shouldLock = this._scrollLockConcerns.size > 0;
    if (wasLocked !== shouldLock) {
      window.$app.logger.log(`ModalService: executing scroll ${action} for ${concern}`);
      const rootEl = document.querySelector('.root');
      if (shouldLock) {
        rootEl?.setAttribute('inert', '');
        document.body.classList.add('no-scroll');
      } else {
        rootEl?.removeAttribute('inert');
        document.body.classList.remove('no-scroll');
      }
    }
  }

  /**
   * Subscribe to modal activity changes. The callback is invoked whenever the
   * modal-active state transitions (active/queued → empty, or empty → active).
   * @returns An unsubscribe function.
   */
  public onModalActivityChange(cb: (hasActiveModal: boolean) => void): () => void {
    this._modalActivityListeners.add(cb);
    return () => this._modalActivityListeners.delete(cb);
  }

  /** Whether any modal is currently active or queued. */
  public get hasActiveOrQueuedModal(): boolean {
    return this._activeModal !== undefined || this._modalQueue.size > 0 || this._showQueue.length > 0;
  }

  private _notifyModalActivity(): void {
    const hasActive = this.hasActiveOrQueuedModal;
    for (const cb of this._modalActivityListeners) cb(hasActive);
  }

  private _subscribeToRouteChanges() {
    if (this._isSubscribedToRouteChanges) {
      return;
    }
    this._isSubscribedToRouteChanges = true;
    // Cold load: the initial store value already reflects the cashier URL before the first emission.
    this._isPaused = this._isOnCashierRoute();

    App.instance.routerStore.subscribe(() => {
      // Incentive modal can be open on game page and then needs to be closed when navigating away by a rich text link.
      this.closeCurrentType(ModalType.IncentiveModal, true);
      this.closeCurrentType(ModalType.GameDiscovery, true);
      this.closeCurrentType(ModalType.CampaignTaC, true);
      this.closeCurrentType(ModalType.RewardDetails, true);
      this.closeCurrentType(ModalType.OptOutModal, true);
      this.closeCurrentType(ModalType.ConfirmLocationModal, true);
      // Remove focus trap as close event might not be triggered for all modals in case of route change
      this._removeFocusTrap();

      if (this._isOnCashierRoute()) {
        this._isPaused = true;
        this._closeDroppedModals();
      } else if (this._isPaused) {
        void this._resumeAfterCashier();
      }
    });
  }

  private _isOnCashierRoute(): boolean {
    return isCashierRouteUrl(App.instance.routerStore.value.url);
  }

  private _isHeld(type: ModalType): boolean {
    return this._isPaused && !cashierPauseExemptModalTypes.has(type);
  }

  private _isDropped(type: ModalType): boolean {
    return this._cashierDroppedTypes.has(type) && this._isOnCashierRoute();
  }

  private _closeDroppedModals(): void {
    const types = [this._activeModal?.type, ...this._modalQueue.keys()].filter(
      (type): type is ModalType => type !== undefined && this._cashierDroppedTypes.has(type),
    );
    for (const type of types) {
      window.$app.logger.log(`ModalService: entering cashier => close '${type}'`);
      this.closeCurrentType(type);
    }
  }

  private async _resumeAfterCashier(): Promise<void> {
    const root = this._getRoot();
    const host = (root instanceof ShadowRoot ? root.host : root) as LitElement;
    // Flush after app-root commits the new root so the focus trap targets it (IGM-4148).
    await host.updateComplete;
    if (!this._isPaused || this._isOnCashierRoute()) {
      return;
    }
    this._isPaused = false;
    window.$app.logger.log('ModalService: left cashier => show held modals');
    this._showQueuedModal();
  }

  private _processShowQueue() {
    window.$app.logger.log('ModalService: _processShowQueue()…');
    if (this._showQueue.length === 0) {
      window.$app.logger.log('ModalService: _processShowQueue empty => done');
      return;
    }
    if (this._showInProgress) {
      window.$app.logger.log('ModalService: _processShowQueue busy => skipped');
      return;
    }
    this._showInProgress = true;
    const { modal, type } = this._showQueue.shift()!;
    this._toBeShownModal = modal;
    this._show(modal, type);
    tick().then(() => {
      this._showInProgress = false;
      this._processShowQueue();
    });
  }

  private _show(modal: UIModalServiceModal, type: ModalType): void {
    window.$app.logger.log(`ModalService: _show '${type}'`);
    if (this._isHeld(type)) {
      this._toBeShownModal = null;
      // Catches items pushed to the show queue before the pause began.
      if (this._isDropped(type)) {
        window.$app.logger.log(`ModalService: on cashier route => drop '${type}'`);
        return;
      }
      const wasIdle = this._activeModal === undefined && this._modalQueue.size === 0;
      this._modalQueue.set(type, modal);
      window.$app.logger.log(`ModalService: paused on cashier => hold '${type}'`);
      if (wasIdle) {
        this._notifyModalActivity();
      }
      return;
    }
    storyService.closeAll(); // TODO: use eventBus?

    // No active modal => show
    if (!this._activeModal) {
      window.$app.logger.log('ModalService: no active modal => show');
      this._appendModal(type, modal);
      return;
    }

    // Replace modal of same id
    if (this._activeModal.type === type) {
      window.$app.logger.log('ModalService: replace modal of same id');
      this._closeActiveModal().then(() => this._appendModal(type, modal));
      return;
    }

    // Active modal of different type.
    // Swap modals if priority of new one is higher.
    const activePriority = prioritization.indexOf(this._activeModal.type);
    const newPriority = prioritization.indexOf(type);
    if (newPriority !== -1 && newPriority > activePriority) {
      this._queueActiveModal();
      this._appendModal(type, modal);
      return;
    }

    // Add modal to queue.
    this._modalQueue.set(type, modal);
    window.$app.logger.log('ModalService: added modal to queue:', type, this._modalQueue.size);
  }

  private _closeActiveModal(hideOnly?: boolean): Promise<void> {
    if (hideOnly) {
      this._hideActiveModal();
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      if (this._activeModal === undefined) {
        window.$app.logger.warn('close active modal failed. modal n/a');
        resolve();
        return;
      }
      window.$app.logger.log(`ModalService close active modal (type: ${this._activeModal.type})…`);

      let resolved = false;

      const resolveHandler = () => {
        if (resolved) return;
        resolved = true;
        this._activeModal?.modal.removeEventListener(UIModal.CLOSE_EVENT, resolveHandler);
        resolve();
      };

      // Safety fallback: resolve if animationend never fires (e.g. close called during open-delay).
      setTimeout(resolveHandler, 600);

      this._activeModal.modal.addEventListener(UIModal.CLOSE_EVENT, resolveHandler);
      this._activeModal.modal.close();
    });
  }

  private _removeActiveModal(): void {
    if (this._activeModal === undefined) {
      window.$app.logger.warn('ModalService: remove active modal failed. modal n/a');
      return;
    }
    window.$app.logger.log(`ModalService: remove active modal (type: ${this._activeModal.type})`);
    this._activeModal.modal.remove();
    this._activeModal = undefined;
    this._notifyModalActivity();
  }

  private _hideActiveModal(): void {
    if (this._activeModal === undefined) {
      window.$app.logger.warn('ModalService: hide active modal failed. modal n/a');
      return;
    }
    window.$app.logger.log(`ModalService: hide active modal (type: ${this._activeModal.type})`);
    this._activeModal.modal.classList.add('hidden');
    this._activeModal = undefined;
  }

  /**
   * Show next modal in queue
   */
  private _showQueuedModal() {
    window.$app.logger.log('ModalService: show queued modal. number of queued modals:', this._modalQueue.size);
    if (this._modalQueue.size === 0) {
      window.$app.logger.log('queue is empty');
      this._notifyModalActivity();
      return;
    }
    if (this._activeModal) {
      window.$app.logger.log('active modal set => skip');
      return;
    }
    const candidates = [...this._modalQueue.keys()].filter((t) => !this._isHeld(t));
    if (candidates.length === 0) {
      window.$app.logger.log('ModalService: paused on cashier => keep queued modals');
      return;
    }

    // Get modal with highest priority
    let typeOfHighestPrio: ModalType | undefined;
    let highestPriorityIndex = -1;
    for (const type of candidates) {
      const prioIndex = prioritization.indexOf(type);
      if (!typeOfHighestPrio) {
        typeOfHighestPrio = type;
        highestPriorityIndex = prioIndex;
        continue;
      }
      if (prioIndex !== -1 && prioIndex > highestPriorityIndex) {
        typeOfHighestPrio = type;
        highestPriorityIndex = prioIndex;
      }
    }

    const type = typeOfHighestPrio!;
    const modal = this._modalQueue.get(type)!;
    window.$app.logger.log(`ModalService: remove queued modal of type: '${type}' from queue`);
    this._modalQueue.delete(type);
    window.$app.logger.log('next modal type:', type, '• remaining modals in queue:', this._modalQueue.size);
    this._appendModal(type, modal);
  }

  private _appendModal(type: ModalType, modal: UIModalServiceModal): void {
    window.$app.logger.log(`ModalService append modal '${type}'`);
    this._addFocusTrap();

    const root = this._getRoot();
    const isInDom = root.contains(modal);

    if (!isInDom) {
      root.appendChild(modal);
    }

    if (this._isModalHidden(modal)) {
      modal.classList.remove('hidden');
    }

    if (this._activeModal !== undefined) {
      window.$app.logger.log(
        'ModalService: Append modal while another modal is active. This should not happen. Check the show logic for potential issues.',
        { newModalType: type, activeModalType: this._activeModal.type },
      );
      this._queueActiveModal();
    }

    this._activeModal = { modal, type };
    this._toBeShownModal = null;
    this._notifyModalActivity();

    const closeHandler = () => {
      window.$app.logger.log("ModalService: 'modal closed' event caught => remove current modal and show queued modal");
      this._removeFocusTrap();

      if (this._modalListeners.has(modal)) {
        modal.removeEventListener(UIModal.CLOSE_EVENT, closeHandler);
        this._modalListeners.delete(modal);
      }

      if (modal === this._activeModal?.modal) {
        this._removeActiveModal();
      } else {
        modal.remove();
      }

      if (this._triggerElement?.focus) {
        this._triggerElement.focus();
      }

      this._showQueuedModal();
    };

    this._modalListeners.set(modal, closeHandler);
    modal.addEventListener(UIModal.CLOSE_EVENT, closeHandler);
  }

  private _isModalHidden(modal: UIModalServiceModal) {
    return modal.classList.contains('hidden');
  }

  private _queueActiveModal(): void {
    if (this._activeModal === undefined) {
      window.$app.logger.log('no active modal to queue => skip');
      return;
    }
    window.$app.logger.log(`queue active modal (type: '${this._activeModal?.type}')`);
    this._queueModal(this._activeModal.modal, this._activeModal.type);
    this._closeActiveModal(true);
  }

  private _queueModal(modal: UIModalServiceModal, type: ModalType): void {
    window.$app.logger.log(`queue modal (type: '${type}')`);
    this._modalQueue.set(type, modal);
  }

  private _removeModalCompletely(type: ModalType) {
    if (this._toBeShownModal) {
      const root = this._getRoot();
      if (root.contains(this._toBeShownModal)) root.removeChild(this._toBeShownModal);
      this._toBeShownModal = null;
    }

    // Remove active modal
    if (this._activeModal) {
      if (this._activeModal.type === type) {
        const modal = this._activeModal.modal;
        const listener = this._modalListeners.get(modal);
        if (listener) {
          modal.removeEventListener(UIModal.CLOSE_EVENT, listener);
          this._modalListeners.delete(modal);
        }
        const parent = modal.parentNode;
        if (parent) parent.removeChild(modal);
        this._activeModal = undefined;
      }
    }

    // Remove queued modal
    const queuedModal = this._modalQueue.get(type);
    if (queuedModal) {
      const listener = this._modalListeners.get(queuedModal);
      if (listener) {
        queuedModal.removeEventListener(UIModal.CLOSE_EVENT, listener);
        this._modalListeners.delete(queuedModal);
      }
      const parent = queuedModal.parentNode;
      if (parent) parent.removeChild(queuedModal);
      this._modalQueue.delete(type);
    }
  }

  /**
   * Add a focus trap to the root element of the application.
   */
  private _addFocusTrap(): void {
    window.$app.logger.log('add focus trap');
    const firstChild = this._getRoot().firstElementChild;
    if (firstChild === null) {
      window.$app.logger.warn('cannot add focus trap. no first child');
      return;
    }
    // Add 'inert' to hide the main content from keyboard focus.
    firstChild.setAttribute('inert', '');
  }

  /**
   * Remove the focus trap from the root element of the application.
   */
  private _removeFocusTrap(): void {
    window.$app.logger.log('remove focus trap');
    const firstChild = this._getRoot().firstElementChild;
    if (firstChild === null) {
      window.$app.logger.warn('cannot remove focus trap. no first child');
      return;
    }
    firstChild.removeAttribute('inert');
  }

  /**
   * @returns The root element of the application.
   */
  private _getRoot(): LitElement | ShadowRoot {
    if (!this._root) {
      this._root = document.querySelector('app-root') as LitElement;
      if (this._root === null) {
        window.$app.logger.warn('cannot select root element!');
        throw new Error('Cannot Select Root Element');
      }
      if (this._root.shadowRoot !== null) {
        this._root = this._root.shadowRoot;
      }
    }
    return this._root;
  }
}

export const BodyScrollLockConcern = {
  slideIn: 'slideIn',
  modal: 'modal',
  gamification: 'gamification',
  story: 'story',
} as const;
export type BodyScrollLockConcern = keyof typeof BodyScrollLockConcern;
